﻿import { h, Fragment, FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import styles from './KPIVisualiser.module.scss';
import KpiVisualiserContext, { useLocalized } from './KPIVisualiser_Context';
import { dateObjToUtcDate, dateToXPos, DAY_WIDTH, ENTRY_HEIGHT, ENTRY_MARGIN_TOP, ENTRY_PADDING, getKpiStateLabel } from './Utils';

interface Props {
    entry: KPIRuntime;
    index: Integer;
    zoom: number;
    scrollX: number;
    isSelected: boolean;
}

export const KpiVisualiser_Entry: FunctionComponent<Props> = memo(({ entry, index, zoom, scrollX, isSelected }) => {
    const contractualDeadlineStr = useLocalized("lbl:contractualDeadline", "Contractual Deadline");
    const agreedDeadlineStr = useLocalized("lbl:agreedDeadline", "Agreed Deadline");
    const unknownKpiStr = useLocalized("lbl:unknownKpi", "-- Unknown KPI --");
    const nonWorkingTooltipStr = useLocalized("lbl:nonWorkingTooltip", "Job was outside of working hours for {0} days.");
    const jobFrozenTooltipStr = useLocalized("lbl:jobFrozenTooltip", "Job was frozen for {0} days.");

    const onClick = () => {
        KpiVisualiserContext.pageReference?.invokeMethodAsync('JS_SelectKPI', entry.id);
    }

    const now = new Date();
    let startDate = dateObjToUtcDate(entry.startDate);
    let actualStartDate = dateObjToUtcDate(entry.actualStartDate);
    const endDate = entry.endDate ? dateObjToUtcDate(entry.endDate) : null;
    if (endDate && endDate < startDate) {
        startDate = dateObjToUtcDate(entry.actualStartDate);

        if (startDate.getFullYear() < 2000) {
            startDate = new Date(endDate.getTime() - (1000 * 60));
        }
    }
    const deadlineDate = entry.targetDeadlineDate ? dateObjToUtcDate(entry.targetDeadlineDate) : new Date(startDate.getTime() + ((entry.kpiDefinition?.greenThreshold ?? 2) * 1000 * 60 * 60 * 24));
    const agreedDeadlineDate = entry.agreedDeadlineDate ? dateObjToUtcDate(entry.agreedDeadlineDate) : null;
    const frozenWidth = startDate < now ? (entry.frozenDays ?? 0) * (DAY_WIDTH * zoom) : 0;

    let titleText = entry.kpiDefinition?.displayName ?? unknownKpiStr;

    let x = dateToXPos(startDate, zoom);
    if (entry.isPrediction && startDate < now) {
        x = dateToXPos(now, zoom);
    }
    let y = ENTRY_MARGIN_TOP + index * (ENTRY_HEIGHT + ENTRY_PADDING * 2) - ENTRY_PADDING;
    const barEntryHeight = ENTRY_HEIGHT / 2;

    //const isSelected = KPIVisualiserContext.selectedEntry === entry.id;

    titleText += `\n${getKpiStateLabel(startDate, agreedDeadlineDate ?? deadlineDate, endDate, entry.isPrediction)}`;

    if (entry.nonWorkingDays && entry.nonWorkingDays > 0) {
        titleText += `\n${nonWorkingTooltipStr.replace("{0}", entry.nonWorkingDays.toString())}`;
    }

    if (entry.frozenDays && entry.frozenDays > 0) {
        titleText += `\n${jobFrozenTooltipStr.replace("{0}", entry.frozenDays.toString())}`;
    }

    const contractualBarY = agreedDeadlineDate ? 0 : ENTRY_HEIGHT / 4.0;

    return (
        <g class={`${styles.kpiEntry} ${isSelected ? styles.selected : ""}`}>
            <rect x={-scrollX} y={y - ENTRY_PADDING} width={99999} height={ENTRY_HEIGHT + ENTRY_PADDING * 2.0} class={styles.selectionBackground} />

            <g transform={`translate(${x}, ${y})`} class={styles.kpiEntryBars} onClick={onClick} cursor="pointer">
                <title>{titleText}</title>

                <KPIVisualiser_EntryBar blockX={x} y={contractualBarY} height={barEntryHeight} startDate={startDate} actualStartDate={actualStartDate} endDate={endDate}
                    deadlineDate={deadlineDate} frozenWidth={frozenWidth} label={contractualDeadlineStr} zoom={zoom}
                    isPrediction={entry.isPrediction} isAgreedDeadline={false} />

                {agreedDeadlineDate && (
                    <KPIVisualiser_EntryBar blockX={x} y={barEntryHeight} height={barEntryHeight} startDate={startDate} actualStartDate={actualStartDate} endDate={endDate}
                        deadlineDate={agreedDeadlineDate} frozenWidth={frozenWidth} label={agreedDeadlineStr} zoom={zoom}
                        isPrediction={entry.isPrediction} isAgreedDeadline={true} />
                )}
            </g>
        </g>
    );
});

interface BarProps {
    blockX: number;
    y: number;
    height: number;
    startDate: Date;
    actualStartDate: Date;
    endDate: Date | null;
    deadlineDate: Date;
    frozenWidth: number;
    label: string;
    zoom: number;
    isPrediction: boolean;
    isAgreedDeadline: boolean;
}

const KPIVisualiser_EntryBar: FunctionComponent<BarProps> = memo((props) => {
    const now = new Date();

    let stateClass = "";
    let width = 0;

    let breachBoxLocalX = 0;
    let breachBoxWidth = 0;
    let extendedTailEndpoint = 0;
    let leadingTailStartpoint = 0;
    let agreedDeadlinePreStartDateLocalX = 0;
    let agreedDeadlinePreStartDateBoxWidth = 0;
    let extendedTailStartpointX = 0;

    if (!props.isPrediction && props.endDate != null && props.endDate < now) {
        width = Math.abs(dateToXPos(props.endDate, props.zoom) - props.blockX);

        if (props.deadlineDate < props.endDate) { // Is it late?
            stateClass = styles.late;
            breachBoxLocalX = dateToXPos(props.deadlineDate, props.zoom) - props.blockX;
            breachBoxWidth = width - breachBoxLocalX;
        } else {
            stateClass = styles.completed;

            if (props.deadlineDate > props.endDate) {
                extendedTailEndpoint = dateToXPos(props.deadlineDate, props.zoom) - props.blockX;
            }
        }

    } else if ((props.startDate > now && props.deadlineDate >= props.startDate) || props.isPrediction) {
        width = Math.abs(dateToXPos(props.deadlineDate, props.zoom) - props.blockX);
        stateClass = styles.future;
    } else if (props.deadlineDate >= props.startDate) {
        width = Math.abs(dateToXPos(now, props.zoom) - props.blockX);
        extendedTailEndpoint = dateToXPos(props.deadlineDate, props.zoom) - props.blockX;

        if (props.deadlineDate < now) {
            stateClass = styles.late;
        }
    }

    if (!props.isPrediction) {
        if (props.actualStartDate < props.startDate) {
            leadingTailStartpoint = dateToXPos(props.actualStartDate, props.zoom) - props.blockX;
        }

        //Drawing of bars if the agreed deadline is set to finish before the start date
        //Draw the bars from the created date of the KPI to the agreed deadline date
        if (props.isAgreedDeadline && props.deadlineDate < props.startDate) {
            agreedDeadlinePreStartDateLocalX = dateToXPos(props.actualStartDate, props.zoom) - props.blockX;

            //Calculation for width of bars if KPI is late and now date is prior to the start date of the KPI
            if (now >= props.deadlineDate && now < props.startDate) {
                agreedDeadlinePreStartDateBoxWidth = Math.abs(agreedDeadlinePreStartDateLocalX) - Math.abs(dateToXPos(props.deadlineDate, props.zoom) - props.blockX);
                breachBoxLocalX = dateToXPos(props.deadlineDate, props.zoom) - props.blockX;
                var nowX = Math.abs(dateToXPos(now, props.zoom) - props.blockX);
                breachBoxWidth = (Math.abs(agreedDeadlinePreStartDateLocalX) - nowX) - agreedDeadlinePreStartDateBoxWidth;
                stateClass = styles.late;
            }
            //Calculation for width of bars if KPI is late and now date is past the start date of the KPI
            else if (now >= props.deadlineDate) {
                agreedDeadlinePreStartDateBoxWidth = Math.abs(agreedDeadlinePreStartDateLocalX) - Math.abs(dateToXPos(props.deadlineDate, props.zoom) - props.blockX);
                breachBoxLocalX = dateToXPos(props.deadlineDate, props.zoom) - props.blockX;
                breachBoxWidth = Math.abs(breachBoxLocalX) + Math.abs(dateToXPos(now, props.zoom) - props.blockX);
                stateClass = styles.late;
            }
            else {
                agreedDeadlinePreStartDateBoxWidth = Math.abs(agreedDeadlinePreStartDateLocalX - (dateToXPos(now, props.zoom) - props.blockX));
                extendedTailStartpointX = dateToXPos(now, props.zoom) - props.blockX;
                extendedTailEndpoint = dateToXPos(props.deadlineDate, props.zoom) - props.blockX;
            }
        }
    }

    const typeIconBgWidth = 22;
    const typeIconBgArcSize = 6.0;
    const kpiTypeIconBgPath = `M0,${props.y + 2.0} v${props.height - 4.0} h${-typeIconBgWidth + typeIconBgArcSize} a${typeIconBgArcSize},${typeIconBgArcSize},0,0,1,${-typeIconBgArcSize},${-typeIconBgArcSize} v${-(props.height - 4.0 - typeIconBgArcSize * 2.0)} a${typeIconBgArcSize},${typeIconBgArcSize},0,0,1,${typeIconBgArcSize},${-typeIconBgArcSize} h${typeIconBgWidth - typeIconBgArcSize} z`;
    const kpiTypeIconAgreedDeadlinePreStartDateBgPath = `M${agreedDeadlinePreStartDateLocalX},${props.y + 2.0} v${props.height - 4.0} h${-typeIconBgWidth + typeIconBgArcSize} a${typeIconBgArcSize},${typeIconBgArcSize},0,0,1,${-typeIconBgArcSize},${-typeIconBgArcSize} v${-(props.height - 4.0 - typeIconBgArcSize * 2.0)} a${typeIconBgArcSize},${typeIconBgArcSize},0,0,1,${typeIconBgArcSize},${-typeIconBgArcSize} h${typeIconBgWidth - typeIconBgArcSize} z`;

    return (
        <g class={stateClass}>
            {agreedDeadlinePreStartDateLocalX !== 0 && (
                <rect x={agreedDeadlinePreStartDateLocalX} y={props.y} width={agreedDeadlinePreStartDateBoxWidth} height={props.height} class={styles.containerBox} />
            )}

            {/* KPI Type Icon for runtimes that have an agreed deadline before the start date*/}
            {agreedDeadlinePreStartDateLocalX !== 0 && (
                <g>
                    <title>{props.label}</title>
                    <path d={kpiTypeIconAgreedDeadlinePreStartDateBgPath} class={styles.typeIconBackground} />
                    <use href={`#kpi_type_${props.isAgreedDeadline ? "agreed" : "contractual"}`} class={styles.typeIcon}
                        x={agreedDeadlinePreStartDateLocalX - 16.0} y={props.y + 4.0} width={12.0} height={props.height - 8.0} />
                </g>
            )}

            {agreedDeadlinePreStartDateLocalX == 0 && (
                <g>
                    <title>{props.label}</title>
                    <path d={kpiTypeIconBgPath} class={styles.typeIconBackground} />
                    <use href={`#kpi_type_${props.isAgreedDeadline ? "agreed" : "contractual"}`} class={styles.typeIcon}
                        x={-16.0} y={props.y + 4.0} width={12.0} height={props.height - 8.0} />
                </g>
            )}

            {/* Background box */}
            {agreedDeadlinePreStartDateLocalX == 0 && (
                <rect x={0} y={props.y} width={width} height={props.height} class={styles.containerBox} />
            )}

            {breachBoxWidth > 0 && (
                <rect x={breachBoxLocalX} y={props.y} width={breachBoxWidth} height={props.height} class={styles.breachBox} />
            )}

            {leadingTailStartpoint !== 0 && agreedDeadlinePreStartDateLocalX == 0  && (
                <>
                    <line class={styles.entryTailBar} x1={leadingTailStartpoint} y1={props.y} x2={leadingTailStartpoint} y2={props.y + props.height} />
                    <line class={styles.entryTailLine} x1={-22.0} y1={props.y + props.height / 2} x2={leadingTailStartpoint} y2={props.y + props.height / 2} />
                </>
            )}

            {extendedTailEndpoint > 0 && agreedDeadlinePreStartDateLocalX == 0 && (
                <>
                    <line x1={width} y1={props.y + props.height / 2} x2={extendedTailEndpoint} y2={props.y + props.height / 2} class={styles.entryTailLine} />
                    <line x1={extendedTailEndpoint} y1={props.y} x2={extendedTailEndpoint} y2={props.y + props.height} class={styles.entryTailBar} />
                </>
            )}

            {extendedTailEndpoint != 0 && agreedDeadlinePreStartDateLocalX != 0 && (
                <>
                    <line x1={extendedTailStartpointX} y1={props.y + props.height / 2} x2={extendedTailEndpoint} y2={props.y + props.height / 2} class={styles.entryTailLine} />
                    <line x1={extendedTailEndpoint} y1={props.y} x2={extendedTailEndpoint} y2={props.y + props.height} class={styles.entryTailBar} />
                </>
            )}

            {props.frozenWidth > 0 && (
                <rect x={0} y={props.y} width={Math.min(props.frozenWidth, width)} height={props.height} class={styles.frozenBox} />
            )}

            {/* Border outline */}
            {agreedDeadlinePreStartDateLocalX == 0 && (
                <rect x={0} y={props.y} width={width} height={props.height} class={styles.containerBoxStroke} />
            )}

            {/* Border outline */}
            {agreedDeadlinePreStartDateLocalX != 0 && (
                <rect x={agreedDeadlinePreStartDateLocalX} y={props.y} width={agreedDeadlinePreStartDateBoxWidth} height={props.height} class={styles.containerBoxStroke} />
            )}

            {/* Used to catch clicks if the runtime width is too short. */}
            <rect x={0} y={props.y} width={Math.max(width, extendedTailEndpoint, 64)} height={props.height} fill="transparent" stroke="none" />
        </g>
    );
});